// src/pages/Logs.js
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { apiURL } from "../config/config";
import {
  FaSync,
  FaEdit,
  FaTrash,
  FaCheck,
  FaTimes,
  FaUnlock,
  FaBan,
  FaPlus,
  FaTimesCircle,
} from "react-icons/fa";
import { toast } from "react-toastify";
import LoadingOverlay from "../components/LoadingOverlay";
import BackButton from "../components/BackButton";

const actionLabelMap = {
  project_created: "สร้าง Project",
  project_renamed: "เปลี่ยนชื่อ Project",
  project_archived: "เก็บ Project ถาวร",
  detail_created: "เพิ่มรายละเอียดงาน",
  detail_renamed: "เปลี่ยนชื่อรายละเอียดงาน",
  detail_archived: "เก็บรายละเอียดงานถาวร",
  entry_created: "เพิ่มชั่วโมงทำงาน",
  entry_updated: "แก้ไขชั่วโมงทำงาน",
  entry_deleted: "ลบชั่วโมงทำงาน",
  submission_created: "ส่ง Timesheet",
  submission_withdrawn: "ถอนการส่ง Timesheet",
  submission_approved: "อนุมัติ Timesheet",
  submission_rejected: "ไม่อนุมัติ Timesheet",
  submission_final_approved: "อนุมัติ Timesheet ครบทุกขั้น",
  period_reopened: "เปิด Timesheet ย้อนหลัง",
};

const getActionLabel = (action) => {
  const rawAction = String(action || "");
  return actionLabelMap[rawAction.toLowerCase()] || rawAction;
};

function Logs() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);

  const [actionFilter, setActionFilter] = useState("");
  const [moduleFilter, setModuleFilter] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [qtFilter, setQtFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${apiURL}logs?`;
      if (startDate) url += `startDate=${startDate}&`;
      if (endDate) url += `endDate=${endDate}&`;
      if (moduleFilter) url += `module=${moduleFilter}&`;

      const res = await axios.get(url);
      setLogs(res.data);
    } catch (err) {
      console.error(err);
      toast.error("ไม่สามารถโหลด Logs ได้");
    }
    setLoading(false);
  }, [endDate, moduleFilter, startDate]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleClearFilters = () => {
    setStartDate("");
    setEndDate("");
    setActionFilter("");
    setModuleFilter("");
    setUserFilter("");
    setQtFilter("");
  };

  const filteredLogs = logs.filter((log) => {
    const matchAction =
      actionFilter === "" ||
      String(log.action || "").toLowerCase() === actionFilter.toLowerCase();
    const matchUser =
      userFilter === "" ||
      log.performedBy?.toLowerCase().includes(userFilter.toLowerCase());
    const matchQt =
      qtFilter === "" ||
      (log.quotationId &&
        log.quotationId.toLowerCase().includes(qtFilter.toLowerCase())) ||
      (log.description &&
        log.description.toLowerCase().includes(qtFilter.toLowerCase()));

    return matchAction && matchUser && matchQt;
  });

  const getIcon = (action) => {
    switch (String(action || "").toLowerCase()) {
      case "edit":
      case "project_renamed":
      case "detail_renamed":
      case "entry_updated":
        return <FaEdit className="text-yellow-500 inline-block mr-1" />;
      case "delete":
      case "entry_deleted":
        return <FaTrash className="text-red-500 inline-block mr-1" />;
      case "approve":
      case "submission_approved":
      case "submission_final_approved":
        return <FaCheck className="text-green-500 inline-block mr-1" />;
      case "reject":
      case "submission_rejected":
        return <FaTimes className="text-red-500 inline-block mr-1" />;
      case "unlock":
      case "period_reopened":
        return <FaUnlock className="text-purple-500 inline-block mr-1" />;
      case "cancel":
        return <FaBan className="text-gray-500 inline-block mr-1" />;
      case "create":
      case "project_created":
      case "detail_created":
      case "entry_created":
      case "submission_created":
        return <FaPlus className="text-blue-500 inline-block mr-1" />;
      case "submission_withdrawn":
        return <FaUnlock className="text-gray-500 inline-block mr-1" />;
      case "project_archived":
      case "detail_archived":
        return <FaTrash className="text-gray-500 inline-block mr-1" />;
      default:
        return <FaCheck className="text-green-500 inline-block mr-1" />;
    }
  };

  return (
    <div className="px-4 sm:px-6 py-6 relative bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors duration-300">
      <LoadingOverlay loading={loading} />

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-3">
          <BackButton label="ย้อนกลับ" />
        </div>

        <div>
          <h1 className="text-[26px] font-semibold text-gray-900 dark:text-gray-100 leading-tight">
            Logs
          </h1>
          <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
            ตรวจสอบประวัติการดำเนินการทั้งหมดในระบบ เช่น การสร้าง แก้ไข อนุมัติ หรือยกเลิกใบเสนอราคา
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl shadow-sm p-4 sm:p-5 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          {/* Date & Filters */}
          <div className="flex flex-wrap items-center gap-2">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition"
            />
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition"
            />

            {/* Action */}
            <select
              className="border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition"
              value={actionFilter}
              onChange={(e) => setActionFilter(e.target.value)}
            >
              <option value="">All Actions</option>
              <option value="create">Create</option>
              <option value="edit">Edit</option>
              <option value="approve">Approve</option>
              <option value="reject">Reject</option>
              <option value="cancel">Cancel</option>
              <option value="unlock">Unlock</option>
              <option value="delete">Delete</option>
              <option value="project_created">สร้าง Project</option>
              <option value="project_renamed">เปลี่ยนชื่อ Project</option>
              <option value="project_archived">เก็บ Project ถาวร</option>
              <option value="detail_created">เพิ่มรายละเอียดงาน</option>
              <option value="detail_renamed">เปลี่ยนชื่อรายละเอียดงาน</option>
              <option value="detail_archived">เก็บรายละเอียดงานถาวร</option>
              <option value="entry_created">เพิ่มชั่วโมงทำงาน</option>
              <option value="entry_updated">แก้ไขชั่วโมงทำงาน</option>
              <option value="entry_deleted">ลบชั่วโมงทำงาน</option>
              <option value="submission_created">ส่ง Timesheet</option>
              <option value="submission_withdrawn">ถอนการส่ง Timesheet</option>
              <option value="submission_approved">อนุมัติ Timesheet</option>
              <option value="submission_rejected">ไม่อนุมัติ Timesheet</option>
              <option value="submission_final_approved">อนุมัติ Timesheet ครบทุกขั้น</option>
              <option value="period_reopened">เปิด Timesheet ย้อนหลัง</option>
            </select>

            {/* Module */}
            <select
              className="border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition"
              value={moduleFilter}
              onChange={(e) => setModuleFilter(e.target.value)}
            >
              <option value="">ทั้งหมด</option>
              <option value="quotation">Quotation</option>
              <option value="travel_expense">Travel Expense</option>
              <option value="timesheet">Timesheet</option>
            </select>

            {/* User Filter */}
            <input
              type="text"
              placeholder="Search by User"
              className="border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition"
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
            />

            {/* QT Filter */}
            <input
              type="text"
              placeholder="Search by QT"
              className="border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-400 focus:border-indigo-400 transition"
              value={qtFilter}
              onChange={(e) => setQtFilter(e.target.value)}
            />
          </div>

          {/* Buttons */}
          <div className="flex flex-wrap gap-2">
            <button
              className="flex items-center gap-2 bg-indigo-500 hover:bg-indigo-600 text-white text-sm font-medium px-4 py-2 rounded-md transition disabled:opacity-50"
              onClick={fetchLogs}
              disabled={loading}
            >
              <FaSync className={loading ? "animate-spin" : ""} />
              Refresh
            </button>

            <button
              className="flex items-center gap-2 bg-gray-400 hover:bg-gray-500 text-white text-sm font-medium px-4 py-2 rounded-md transition"
              onClick={handleClearFilters}
            >
              <FaTimesCircle />
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700 rounded-xl shadow-sm overflow-x-auto">
        <table className="min-w-full text-sm text-left border-collapse">
          <thead className="bg-gray-50 dark:bg-gray-700 border-b dark:border-gray-600">
            <tr>
              <th className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-200">#</th>
              <th className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-200">
                Action
              </th>
              <th className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-200">
                Description
              </th>
              <th className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-200">
                User
              </th>
              <th className="px-4 py-3 font-semibold text-gray-600 dark:text-gray-200">
                Timestamp
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredLogs.length > 0 ? (
              filteredLogs.map((log, index) => (
                <tr
                  key={log._id}
                  className="hover:bg-indigo-50 dark:hover:bg-indigo-900/40 border-b dark:border-gray-700 transition"
                >
                  <td className="px-4 py-2 text-gray-700 dark:text-gray-300">
                    {index + 1}
                  </td>
                  <td className="px-4 py-2 font-semibold text-gray-800 dark:text-gray-100 flex items-center gap-1">
                    {getIcon(log.action)}
                    {getActionLabel(log.action)}
                  </td>
                  <td className="px-4 py-2 text-gray-700 dark:text-gray-300">
                    {log.description}
                  </td>
                  <td className="px-4 py-2 text-gray-700 dark:text-gray-300">
                    {log.performedBy}
                  </td>
                  <td className="px-4 py-2 text-gray-700 dark:text-gray-300">
                    {new Date(log.timestamp).toLocaleString("th-TH", {
                      dateStyle: "short",
                      timeStyle: "short",
                    })}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  className="px-4 py-4 text-center text-gray-500 dark:text-gray-400"
                  colSpan="5"
                >
                  ไม่พบข้อมูล Logs
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Logs;
